import React, { useEffect, useState, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useNavigate, useParams } from 'react-router-dom';
import AppHeader from '../AppHeader';
import { useAuth } from '../../contexts/AuthContext';
import {
  COMMUNICATION_CATEGORIES,
  deleteCampaign,
  downloadDeliveriesCsv,
  getCampaign,
  listDeliveries,
  patchCampaign,
  previewCampaign,
  sendCampaign,
} from '../../services/communications';

const STATUS_COLORS = {
  draft: 'default',
  previewed: 'info',
  sending: 'info',
  sent: 'success',
  failed: 'error',
  cancelled: 'error',
};

const CH_COLORS = {
  delivered: 'success',
  sent: 'success',
  queued: 'info',
  pending: 'default',
  failed: 'error',
  skipped: 'default',
};

const DELIVERY_FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'failed', label: 'Failed' },
  { value: 'pending', label: 'Pending' },
  { value: 'skipped', label: 'Skipped' },
];

function deliveryForChannel(deliveries, channel) {
  const list = deliveries || [];
  return list.find((d) => d.channel === channel) || null;
}

export default function CampaignDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const readOnly = user?.role === 'viewer';
  const isAdmin = user?.role === 'superadmin' || user?.role === 'admin';

  const [campaign, setCampaign] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [delCount, setDelCount] = useState(0);
  const [delPage, setDelPage] = useState(0);
  const [delPageSize, setDelPageSize] = useState(25);
  const [delFilter, setDelFilter] = useState('all');
  const [delSearch, setDelSearch] = useState('');
  const [delSearchDebounced, setDelSearchDebounced] = useState('');
  const [loading, setLoading] = useState(true);
  const [delLoading, setDelLoading] = useState(false);
  const [error, setError] = useState(null);
  const [bodyOpen, setBodyOpen] = useState(false);
  const [confirmSendOpen, setConfirmSendOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const loadCampaign = useCallback(async () => {
    const c = await getCampaign(id);
    setCampaign(c);
  }, [id]);

  const loadDeliveries = useCallback(async () => {
    setDelLoading(true);
    try {
      const params = {
        page: delPage + 1,
        page_size: delPageSize,
        search: delSearchDebounced || undefined,
        delivery_status: delFilter === 'all' ? undefined : delFilter,
      };
      const { results, count } = await listDeliveries(id, params);
      setDeliveries(results);
      setDelCount(count);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setDelLoading(false);
    }
  }, [id, delPage, delPageSize, delSearchDebounced, delFilter]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        await loadCampaign();
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadCampaign]);

  useEffect(() => {
    const t = setTimeout(() => setDelSearchDebounced(delSearch.trim()), 350);
    return () => clearTimeout(t);
  }, [delSearch]);

  useEffect(() => {
    if (!campaign) return;
    loadDeliveries();
  }, [campaign, loadDeliveries]);

  const catLabel = (v) => COMMUNICATION_CATEGORIES.find((c) => c.value === v)?.label || v;

  const handleExport = async () => {
    try {
      const blob = await downloadDeliveriesCsv(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `campaign_${id}_deliveries.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  const isDraftLike = campaign && (campaign.status === 'draft' || campaign.status === 'previewed');

  const handleSendFromDetail = async () => {
    if (!campaign || readOnly) return;
    setSending(true);
    setError(null);
    try {
      await patchCampaign(campaign.id, {
        title: campaign.title,
        body_plain: campaign.body_plain,
        category: campaign.category,
        priority: campaign.priority,
        audience_type: campaign.audience_type,
        session: campaign.session,
        classroom_class_num: campaign.classroom_class_num,
        specific_student_ids: campaign.specific_student_ids || [],
        position_group: campaign.position_group || '',
        staff_ids: campaign.staff_ids || [],
      });
      await previewCampaign(campaign.id);
      await sendCampaign(campaign.id);
      setConfirmSendOpen(false);
      setToast('Send queued');
      await loadCampaign();
      await loadDeliveries();
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!campaign) return;
    setDeleting(true);
    setError(null);
    try {
      await deleteCampaign(campaign.id);
      setDeleteOpen(false);
      navigate('/communications');
    } catch (e) {
      const msg =
        (typeof e.response?.data?.detail === 'string' && e.response.data.detail) ||
        e.message ||
        'Delete failed';
      setError(msg);
    } finally {
      setDeleting(false);
    }
  };

  const summary = campaign?.delivery_summary;

  return (
    <Box>
      <AppHeader backTo="/communications" />
      <Container maxWidth="lg" sx={{ mt: 3, mb: 4 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {loading || !campaign ? (
          <Typography>{loading ? 'Loading…' : 'Not found'}</Typography>
        ) : (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 2 }}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  {campaign.title || `Campaign #${campaign.id}`}
                </Typography>
                <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                  <Chip size="small" label={campaign.status} color={STATUS_COLORS[campaign.status] || 'default'} />
                  <Chip size="small" label={catLabel(campaign.category)} variant="outlined" />
                  {campaign.priority === 'emergency' && (
                    <Chip size="small" label="Emergency priority" color="error" variant="outlined" />
                  )}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                {isDraftLike && !readOnly && (
                  <>
                    <Button variant="outlined" onClick={() => navigate(`/communications/${campaign.id}/edit`)}>
                      Edit
                    </Button>
                    <Button variant="contained" onClick={() => setConfirmSendOpen(true)}>
                      Send
                    </Button>
                  </>
                )}
                <Button variant="outlined" onClick={handleExport}>
                  Export CSV
                </Button>
                {isAdmin && (
                  <Tooltip
                    title={campaign.status === 'sending' ? 'Cannot delete while sending' : 'Delete campaign'}
                  >
                    <span>
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteOutlineIcon />}
                        disabled={campaign.status === 'sending'}
                        onClick={() => setDeleteOpen(true)}
                      >
                        Delete
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </Box>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Created by {campaign.created_by_name || campaign.created_by_email || '—'} ·{' '}
              {campaign.created_at ? new Date(campaign.created_at).toLocaleString() : ''}
              {campaign.sent_at && <> · Sent {new Date(campaign.sent_at).toLocaleString()}</>}
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Button size="small" onClick={() => setBodyOpen((o) => !o)}>
                {bodyOpen ? 'Hide' : 'Show'} message body
              </Button>
              <Collapse in={bodyOpen}>
                <Typography variant="body1" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                  {campaign.body_plain || '—'}
                </Typography>
              </Collapse>
            </Paper>

            {summary && (
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {['sms', 'voice'].map((ch) => {
                  const s = summary[ch] || {};
                  return (
                    <Grid item xs={12} sm={6} key={ch}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="subtitle2" color="text.secondary">
                            {ch.toUpperCase()}
                          </Typography>
                          <Typography variant="h6" color="success.main">
                            {s.delivered ?? 0} / {s.total ?? 0}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            delivered / total
                          </Typography>
                          {(s.pending > 0 || s.failed > 0) && (
                            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                              {s.pending ? `${s.pending} pending ` : ''}
                              {s.failed ? `${s.failed} failed` : ''}
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}

            <Typography variant="h6" sx={{ mb: 1 }}>
              Recipients
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Status filter</InputLabel>
                <Select
                  label="Status filter"
                  value={delFilter}
                  onChange={(e) => {
                    setDelFilter(e.target.value);
                    setDelPage(0);
                  }}
                >
                  {DELIVERY_FILTER_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="Search parent name"
                value={delSearch}
                onChange={(e) => {
                  setDelSearch(e.target.value);
                  setDelPage(0);
                }}
              />
            </Box>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Parent</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>SMS</TableCell>
                    <TableCell>Voice</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {delLoading ? (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography variant="body2">Loading…</Typography>
                      </TableCell>
                    </TableRow>
                  ) : deliveries.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography variant="body2" color="text.secondary">
                          No delivery rows (draft or not sent yet).
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    deliveries.map((r) => {
                      const ds = r.deliveries || [];
                      const sms = deliveryForChannel(ds, 'sms');
                      const voice = deliveryForChannel(ds, 'voice');
                      return (
                        <TableRow key={r.id}>
                          <TableCell>{r.parent_name}</TableCell>
                          <TableCell>{r.phone_e164}</TableCell>
                          <TableCell>
                            {sms ? (
                              <Chip
                                size="small"
                                label={sms.delivery_status}
                                color={CH_COLORS[sms.delivery_status] || 'default'}
                                variant="outlined"
                              />
                            ) : (
                              '—'
                            )}
                          </TableCell>
                          <TableCell>
                            {voice ? (
                              <Chip
                                size="small"
                                label={voice.delivery_status}
                                color={CH_COLORS[voice.delivery_status] || 'default'}
                                variant="outlined"
                              />
                            ) : (
                              '—'
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={delCount}
                page={delPage}
                onPageChange={(_, p) => setDelPage(p)}
                rowsPerPage={delPageSize}
                onRowsPerPageChange={(e) => {
                  setDelPageSize(parseInt(e.target.value, 10));
                  setDelPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </TableContainer>
          </>
        )}
      </Container>

      <Dialog open={confirmSendOpen} onClose={() => !sending && setConfirmSendOpen(false)}>
        <DialogTitle>Send campaign?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Queue SMS and voice (TTS) to all recipients matching this campaign&apos;s audience?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSendOpen(false)} disabled={sending}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSendFromDetail} disabled={sending}>
            {sending ? 'Sending…' : 'Confirm send'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => !deleting && setDeleteOpen(false)}>
        <DialogTitle>Delete campaign?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Delete campaign &apos;{campaign?.title || `#${campaign?.id}`}&apos;? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleConfirmDelete} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast('')} message={toast} />
    </Box>
  );
}
